import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export type TrailerPhotoType = Database["public"]["Enums"]["trailer_photo_type"];

export interface TrailerEventPhoto {
  id: string;
  photo_type: TrailerPhotoType;
  image_path: string;
  created_at: string;
  signedUrl: string | null;
}

const SIGNED_URL_TTL_SECONDS = 3600;

export const TRAILER_PHOTO_TYPE_LABEL: Record<TrailerPhotoType, string> = {
  trailer: "Trailer",
  seal: "Seal",
  license_plate: "License Plate",
};

/**
 * Fetch every yard photo captured for this trailer across its full event
 * history, newest first, resolved to short-lived signed URLs from the
 * private `trailer-images` bucket.
 */
export function useTrailerEventPhotos(trailerDbId: string | null | undefined) {
  const { data: rows = [] } = useQuery({
    queryKey: ["trailer-event-photos", trailerDbId],
    enabled: !!trailerDbId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trailer_event_photos")
        .select("id, photo_type, image_path, created_at")
        .eq("trailer_id", trailerDbId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const [signed, setSigned] = useState<TrailerEventPhoto[]>([]);

  useEffect(() => {
    let cancelled = false;
    if (!rows.length) {
      setSigned([]);
      return;
    }
    Promise.all(
      rows.map(async (r) => {
        const { data } = await supabase.storage
          .from("trailer-images")
          .createSignedUrl(r.image_path, SIGNED_URL_TTL_SECONDS);
        return {
          id: r.id,
          photo_type: r.photo_type as TrailerPhotoType,
          image_path: r.image_path,
          created_at: r.created_at,
          signedUrl: data?.signedUrl ?? null,
        };
      }),
    ).then((all) => {
      if (!cancelled) setSigned(all);
    });
    return () => {
      cancelled = true;
    };
  }, [rows]);

  return signed;
}